import React from "react";
import { useResourceImage } from "../lib/useResourceImage";

type PlaceholderVariant = "phone" | "photo" | "square" | "wide" | "qr";

interface AssetPlaceholderProps {
  label: string;
  hint?: string;
  variant?: PlaceholderVariant;
  className?: string;
  resourceKey?: string;
  alt?: string;
  fallbackSrc?: string;
}

const ratioByVariant: Record<PlaceholderVariant, string> = {
  phone: "aspect-[9/18]",
  photo: "aspect-[4/5]",
  square: "aspect-square",
  wide: "aspect-[16/10]",
  qr: "aspect-square",
};

// Visual placeholder for assets the client will provide later (app screens,
// QR code, dealer banner, logo variants). If a local preview was selected
// via /content-admin (stored in localStorage), it renders here automatically.
export const AssetPlaceholder: React.FC<AssetPlaceholderProps> = ({
  label,
  hint,
  variant = "photo",
  className = "",
  resourceKey = "",
  alt,
  fallbackSrc = "",
}) => {
  const { src } = useResourceImage(resourceKey, fallbackSrc);

  if (resourceKey && src) {
    return (
      <div
        data-testid={`asset-placeholder-${variant}`}
        className={`overflow-hidden rounded-2xl ${ratioByVariant[variant]} ${className}`}
      >
        <img src={src} alt={alt || label} className="h-full w-full object-cover" loading="lazy" />
      </div>
    );
  }

  return (
    <div
      data-testid={`asset-placeholder-${variant}`}
      className={`flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-motor-red/30 bg-motor-mist text-center px-6 ${ratioByVariant[variant]} ${className}`}
    >
      <span className="font-display text-[11px] uppercase tracking-[0.18em] text-motor-red">
        {label}
      </span>
      {hint && <span className="text-xs text-neutral-500 max-w-[220px]">{hint}</span>}
    </div>
  );
};
